function getElEmailLandingVideos(scope = document) {
  return Array.from(
    scope.querySelectorAll(".el-email-landing__video, .el-email-landing__mobile-video")
  );
}

function getElEmailLandingVideoParent(video) {
  return video ? video.parentElement : null;
}

function hideElEmailLandingVideoParent(video) {
  const parent = getElEmailLandingVideoParent(video);
  if (!parent) return;

  parent.classList.remove("el-email-landing__video-parent--loaded");
  parent.dataset.elVideoLoaded = "false";
}

function showElEmailLandingVideoParent(video) {
  const parent = getElEmailLandingVideoParent(video);
  if (!parent) return;

  parent.classList.add("el-email-landing__video-parent--loaded");
  parent.dataset.elVideoLoaded = "true";
}

function isElEmailLandingVideoFullyBuffered(video) {
  if (!video) return false;
  // show as soon as the FIRST frame is decoded (HAVE_CURRENT_DATA) instead of
  // waiting for the whole file to buffer — that wait was the 5–10s black screen on mobile
  return video.readyState >= 2;
}

function setupElEmailLandingVideoLoading(scope = document) {
  getElEmailLandingVideos(scope).forEach((video) => {
    hideElEmailLandingVideoParent(video);

    const showWhenLoaded = () => {
      if (video.dataset.elVideoLoaded === "true") return;

      if (!isElEmailLandingVideoFullyBuffered(video)) return;

      showElEmailLandingVideoParent(video);
    };

    showWhenLoaded();

    video.addEventListener("loadedmetadata", showWhenLoaded);
    video.addEventListener("loadeddata", showWhenLoaded);
    video.addEventListener("progress", showWhenLoaded);
    video.addEventListener("canplaythrough", showWhenLoaded);
    video.addEventListener("playing", function () {
      video.classList.add("is-playing");
    });
  });
}

setupElEmailLandingVideoLoading();

// iOS autoplay safety net: start muted videos on first tap + resume when the tab refocuses
(function elEmailLandingAutoplayGuard() {
  function playAll() {
    getElEmailLandingVideos().forEach(function (v) {
      v.muted = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
  }
  ["touchstart", "click"].forEach(function (ev) {
    document.addEventListener(ev, playAll, { once: true, passive: true });
  });
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") playAll();
  });
  window.addEventListener("pageshow", playAll);
})();

class ElEmailLanding {
  constructor(form) {
    this.form = form;
    this.emailInput =
      form.querySelector('input[name="email"]') ||
      form.querySelector('input[type="email"]');

    this.button = form.querySelector('button[type="submit"]');
    this.message = form.querySelector("[data-el-klaviyo-message]");

    this.signup = form.closest(".el-email-landing__signup");
    this.bottom = form.closest(".el-email-landing__bottom");
    this.section = form.closest(".el-email-landing");

    this.formTexts = this.section
      ? this.section.querySelectorAll(".el-email-landing__form-text")
      : [];

    this.mobileFormText = this.section
      ? this.section.querySelector(".el-email-landing__form-text--mobile")
      : null;

    this.desktopFormText = this.section
      ? this.section.querySelector(".el-email-landing__form-text--desktop")
      : null;

    this.bottomText = this.section
      ? this.section.querySelector(".el-email-landing__bottom-text")
      : null;

    this.listId = form.dataset.klaviyoListId;
    this.companyId = form.dataset.klaviyoCompanyId;
    this.source = form.dataset.klaviyoSource || "Email landing page";

    this.buttonDefaultText = this.button ? this.button.textContent.trim() : "ADD ME";
    this.successButtonText = "Invite Your Mates";
    this.successFormText = `
      <span class="freshlist-title">Welcome to the Freshlist.</span>
      <span class="freshlist-subtitle">We'll be in touch.</span>
    `;
    this.successBottomText = "Your spot is locked. Every drop hits the Freshlist first.";

    this.isSuccess = false;
    this.isSubmitting = false;
    this.copyTimeout = null;

    this.init();
  }

  init() {
    this.form.dataset.elKlaviyoReady = "true";

    if (this.emailInput) {
      this.emailInput.required = true;
    }

    this.form.addEventListener("submit", (event) => {
      event.preventDefault();

      if (this.isSuccess) {
        this.copyInviteLink();
        return;
      }

      this.subscribe();
    });
  }

  async subscribe() {
    if (this.isSubmitting) return;

    const email = this.emailInput ? this.emailInput.value.trim() : "";

    if (!this.emailInput || !email || !this.emailInput.checkValidity()) {
      if (this.emailInput) {
        this.emailInput.reportValidity();
      }

      this.setMessage("Please enter a valid email.", "error");
      return;
    }

    if (!this.listId || !this.companyId) {
      console.error("Missing Klaviyo data attributes:", {
        listId: this.listId,
        companyId: this.companyId
      });

      this.setMessage("Klaviyo is not configured correctly.", "error");
      return;
    }

    this.setLoading(true);
    this.setMessage("", "");

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: this.source,
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: "SUBSCRIBED"
                    }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: this.listId
            }
          }
        }
      }
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            revision: "2026-04-15"
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Klaviyo error:", response.status, errorText);
        throw new Error("Klaviyo subscription failed");
      }

      this.form.reset();
      this.setSuccessState();
    } catch (error) {
      console.error(error);
      this.setMessage("Something went wrong. Please try again.", "error");
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (this.isSuccess) return;

    this.isSubmitting = isLoading;

    if (!this.button) return;

    this.button.removeAttribute("disabled");

    if (isLoading) {
      this.button.setAttribute("aria-busy", "true");
    } else {
      this.button.removeAttribute("aria-busy");
    }
  }

  setSuccessState() {
    this.isSuccess = true;
    this.isSubmitting = false;

    if (this.section) {
      this.section.classList.add("el-email-landing--success");
    }

    if (this.bottom) {
      this.bottom.classList.add("el-email-landing__bottom--success");
    }

    if (this.signup) {
      this.signup.classList.add("el-email-landing__signup--success");
    }

    if (this.form) {
      this.form.classList.add("el-email-landing__form--success");
    }

    // Update visible form text
    this.formTexts.forEach((text) => {
      text.innerHTML = this.successFormText;
      text.classList.add("el-email-landing__form-text--success");
      text.removeAttribute("aria-hidden");
      text.style.display = "";
    });

    // Fallback: if no visible form text exists, create one inside the form
    if (this.form && !this.form.querySelector(".el-email-landing__success-text")) {
      const successText = document.createElement("div");
      successText.className = "el-email-landing__success-text";
      successText.innerHTML = this.successFormText;

      this.form.insertBefore(successText, this.form.firstChild);
    }

    // Do not put success text inside the input
    if (this.emailInput) {
      this.emailInput.value = "";
      this.emailInput.disabled = true;
      this.emailInput.required = false;
      this.emailInput.setAttribute("aria-label", "Email submitted successfully");
      this.emailInput.style.display = "none";
    }

    if (this.bottomText) {
      this.bottomText.textContent = this.successBottomText;
    }

    if (this.button) {
      this.button.removeAttribute("disabled");
      this.button.removeAttribute("aria-busy");
      this.button.textContent = this.successButtonText;
      this.button.setAttribute("aria-label", "Copy invite link");
    }

    this.setMessage("", "success");
  }

  async copyInviteLink() {
    const link = "https://getgoodmates.com/";

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        this.fallbackCopy(link);
      }

      this.showCopiedState();
    } catch (error) {
      console.error("Copy failed:", error);
      this.setMessage("Link could not be copied. Please copy it from your browser.", "error");
    }
  }

  fallbackCopy(text) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";

    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  showCopiedState() {
    if (!this.button) return;

    clearTimeout(this.copyTimeout);

    this.button.textContent = "Link copied ✓";

    this.copyTimeout = setTimeout(() => {
      this.button.textContent = this.successButtonText;
    }, 2000);
  }

  setMessage(text, status) {
    if (!this.message) return;

    this.message.textContent = text;
    this.message.dataset.status = status;
  }
}

function initElEmailLanding() {
  document
    .querySelectorAll("[data-el-klaviyo-form]:not([data-el-klaviyo-ready])")
    .forEach((form) => {
      new ElEmailLanding(form);
    });
}

document.addEventListener("DOMContentLoaded", initElEmailLanding);

document.addEventListener("shopify:section:load", (event) => {
  setupElEmailLandingVideoLoading(event.target);
  initElEmailLanding();
});